// src/components/OrderEditor.js
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/Button";
import { getTechnologicalCardsForNomenclature } from "../api/enterprise";
import { notifyError } from "../utils/toasts";

function OrderEditor({
  order,
  nomenclatures,
  /* technologicalCards, */ onSave,
  onDeleteItem,
  divisionId,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(order ? order.name : "");
  const [orderItems, setOrderItems] = useState(
    order ? order.order_items || [] : []
  );
  const [technologicalCards, setTechnologicalCards] = useState({});

  useEffect(() => {
    const fetchTechnologicalCardsForItems = async () => {
      const updatedTechnologicalCards = {};
      for (let index = 0; index < orderItems.length; index++) {
        const item = orderItems[index];
        if (item.nomenclature_id) {
          try {
            const cards = await getTechnologicalCardsForNomenclature(
              divisionId,
              item.nomenclature_id
            );
            updatedTechnologicalCards[index] = cards;
          } catch (error) {
            notifyError(error?.response?.data?.message || error.message);
          }
        }
      }
      setTechnologicalCards(updatedTechnologicalCards);
    };

    fetchTechnologicalCardsForItems();
  }, [orderItems]);

  const handleAddItem = () => {
    setOrderItems([
      ...orderItems,
      { nomenclature_id: "", quantity: 1, product_sheet_id: "" },
    ]); // По умолчанию quantity = 1
  };

  const handleItemChange = async (index, field, value) => {
    const updatedItems = [...orderItems];
    if (field === "quantity") {
      value = Math.max(1, parseInt(value, 10) || 1); // Запрещаем значения меньше 1
    }
    updatedItems[index][field] = value;
    setOrderItems(updatedItems);

    if (field === "nomenclature_id") {
      try {
        const cards = await getTechnologicalCardsForNomenclature(
          divisionId,
          value
        );
        setTechnologicalCards((prev) => ({ ...prev, [index]: cards }));
        // Если есть доступные технологические карты, автоматически выбираем первую
        if (cards.length > 0) {
          updatedItems[index]["product_sheet_id"] = cards[0].id;
          setOrderItems(updatedItems);
        } else {
          // Если нет доступных карт, очищаем значение product_sheet_id
          updatedItems[index]["product_sheet_id"] = "";
          setOrderItems(updatedItems);
        }
      } catch (error) {
        console.error("Error fetching technological cards:", error);
      }
    }
  };

  const handleRemoveItem = async (index) => {
    const item = orderItems[index];
    if (item.order_item_id) {
      try {
        await onDeleteItem(item.order_item_id);
      } catch (error) {
        console.error("Error deleting order item:", error);
        return;
      }
    }
    const updatedItems = orderItems.filter((_, idx) => idx !== index);
    setOrderItems(updatedItems);
  };

  const handleSave = () => {
    if (name.trim()) {
      onSave(name, orderItems);
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-body">
        <h3>{order ? t("edit_order") : t("create_order")}</h3>
        <div className="form-group">
          <label>{t("name")}</label>
          <input
            type="text"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <h4>{t("order_items")}</h4>
        <div className="form-group d-flex align-items-center font-weight-bold">
          <div className="mr-2" style={{ flex: 1 }}>
            {t("nomenclature")}
          </div>
          <div className="mr-2" style={{ width: "9%", marginLeft: "9px" }}>
            {t("quantity")}
          </div>
          <div className="mr-2" style={{ flex: 1 }}>
            {t("technological_card")}
          </div>
          <div className="mr-2" style={{ minWidth: "90px" }}>
            {/* Пустое место для кнопки удаления */}
          </div>
        </div>
        {orderItems.map((item, index) => (
          <div key={index} className="form-group d-flex align-items-center">
            <select
              className="form-control mr-2"
              value={item.nomenclature_id}
              onChange={(e) =>
                handleItemChange(index, "nomenclature_id", e.target.value)
              }
              disabled={!!item.order_item_id} // Запрещаем изменение номенклатуры для уже созданных позиций
            >
              <option value="">{t("select_nomenclature")}</option>
              {nomenclatures.map((nomenclature) => (
                <option
                  key={nomenclature.nomenclature_id}
                  value={nomenclature.nomenclature_id}
                >
                  {nomenclature.name}
                </option>
              ))}
            </select>
            <input
              type="number"
              className="form-control mr-2"
              placeholder={t("quantity")}
              value={item.quantity}
              onChange={(e) =>
                handleItemChange(index, "quantity", e.target.value)
              }
              min="1" // Запрещаем отрицательные значения через атрибут min
              style={{ width: "20%" }}
            />
            <select
              className="form-control mr-2"
              value={item.product_sheet_id}
              onChange={(e) =>
                handleItemChange(index, "product_sheet_id", e.target.value)
              }
            >
              <option value="">---</option>
              {(technologicalCards[index] || []).map((card) => (
                <option key={card.id} value={String(card.id)}>
                  {card.name}
                </option>
              ))}
            </select>
            <Button variant="danger" onClick={() => handleRemoveItem(index)}>
              {t("delete")}
            </Button>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button className="mt-2" onClick={handleAddItem}>
            {t("add_item")}
          </Button>
          <Button className="mt-2" onClick={handleSave}>
            {t("save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default OrderEditor;
